'use client';

import { useMemo } from 'react';
import { BookOpen, User, Users, Plus, MoreHorizontal } from 'lucide-react';
import { Button } from '../ui/button.tsx';
import { generateDepartmentColor } from '../../utils/constants';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
} from '../ui/dropdown.tsx';
import { ScrollArea, ScrollBar } from '../ui/scroll-area.tsx';

export default function DepartmentCards({
  departments = [],
  onAddDepartment,
  onViewDepartment,
  onEditDepartment,
  onDeleteDepartment,
}) {
  // Memoize department colors to keep them consistent
  const departmentColors = useMemo(() => {
    const colorMap = new Map();
    departments.forEach((dept) => {
      const deptId = dept?.id || dept?.deptId;
      if (deptId && !colorMap.has(deptId)) {
        colorMap.set(deptId, generateDepartmentColor());
      }
    });
    return colorMap;
  }, [departments]);

  const getDepartmentStyle = (department) => {
    const deptId = department?.id || department?.deptId;
    if (!deptId) return { bg: '#EEF0FF', text: '#3F3D99' };
    const color = departmentColors.get(deptId) || generateDepartmentColor();
    return color;
  };

  return (
    <div className="w-full">
      <div className="flex md:flex-row flex-col md:items-center gap-3 mb-4 w-full">
        <h2 className="text-lg font-bold flex-grow">Active Departments</h2>
        <Button
          onClick={onAddDepartment}
          className="bg-purple-500 hover:bg-purple-600 text-white rounded-md px-3 py-1.5 h-auto text-sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          New Department
        </Button>
      </div>

      <ScrollArea>
        <div className="flex space-x-3 pb-3">
          {departments.map((department) => {
            const deptId = department?.id || department?.deptId;
            // Skip invalid departments
            if (!deptId || !department?.name) {
              console.warn('Invalid department data:', department);
              return null;
            }

            const { bg, text } = getDepartmentStyle(department);
            return (
              <div
                key={deptId}
                className="flex-1 w-[calc(25%-9px)] rounded-xl p-4 group relative cursor-pointer"
                style={{ backgroundColor: bg }}
                onClick={() => onViewDepartment?.(department)}
              >
                <div className="absolute right-3 top-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      asChild
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditDepartment?.(department);
                        }}
                      >
                        Edit department
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDepartment?.(department);
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex flex-col h-full">
                  <div className="mb-8">
                    <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center">
                      <BookOpen className="h-7 w-7" style={{ color: text }} />
                    </div>
                  </div>

                  <div className="mb-4">
                    <h2
                      className="text-xl font-medium text-[#1A1A1A] truncate"
                      title={department.name}
                    >
                      {department.name}
                    </h2>
                  </div>

                  <div className="mt-auto">
                    <div className="bg-white rounded-lg flex divide-x divide-gray-200">
                      <div className="flex items-center justify-center py-2 px-3 flex-1">
                        <BookOpen className="h-4 w-4 mr-1.5 text-gray-700" />
                        <span className="font-medium text-sm">
                          {department.subjects?.length || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-center py-2 px-3 flex-1">
                        <User className="h-4 w-4 mr-1.5 text-gray-700" />
                        <span className="font-medium text-sm">
                          {department.staffCount || 0}
                        </span>
                      </div>
                      <div className="flex items-center justify-center py-2 px-3 flex-1">
                        <Users className="h-4 w-4 mr-1.5 text-gray-700" />
                        <span className="font-medium text-sm">
                          {department.studentCount || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
